import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { cn } from "../../lib/utils";
import { BlockLink } from "../../render";
import { Button } from "../ui/button";
import { Reveal } from "../ui/reveal";
const VIDEO_FADE_SECONDS = 1.1;
function HeroMedia({ videoSrc, posterSrc }) {
    const motionRef = React.useRef(null);
    const videoRef = React.useRef(null);
    const fadeRef = React.useRef(null);
    React.useEffect(() => {
        const motion = motionRef.current;
        if (!motion ||
            window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            return;
        }
        let frame = 0;
        const update = () => {
            const progress = Math.min(1, Math.max(0, window.scrollY / window.innerHeight));
            const eased = 1 - Math.pow(1 - progress, 3);
            const travel = window.innerHeight * 0.021 * eased;
            motion.style.transform = `translate3d(0, ${-travel}px, 0)`;
        };
        const requestUpdate = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(update);
        };
        update();
        window.addEventListener("scroll", requestUpdate, { passive: true });
        window.addEventListener("resize", requestUpdate);
        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener("scroll", requestUpdate);
            window.removeEventListener("resize", requestUpdate);
        };
    }, []);
    React.useEffect(() => {
        const video = videoRef.current;
        const fade = fadeRef.current;
        if (!video || !fade)
            return;
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            fade.style.opacity = "0";
            return;
        }
        let frame = 0;
        const tick = () => {
            const { currentTime, duration } = video;
            if (duration && Number.isFinite(duration)) {
                const easedIn = Math.min(1, currentTime / VIDEO_FADE_SECONDS);
                const easedOut = Math.min(1, (duration - currentTime) / VIDEO_FADE_SECONDS);
                fade.style.opacity = String(1 - Math.min(easedIn, easedOut));
            }
            frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [videoSrc]);
    return (_jsx("div", { ref: motionRef, className: "will-change-transform motion-reduce:transform-none", children: _jsxs("div", { className: "relative mx-auto h-[220px] w-[300px] overflow-hidden rounded-lg bg-secondary", children: [videoSrc ? (_jsx("video", { ref: videoRef, className: "block size-full object-cover", src: videoSrc, poster: posterSrc, autoPlay: true, muted: true, loop: true, playsInline: true, preload: "auto", "aria-hidden": "true", tabIndex: -1 })) : posterSrc ? (_jsx("img", { className: "block size-full object-cover", src: posterSrc, alt: "", "aria-hidden": "true" })) : (_jsx("div", { className: "size-full bg-[radial-gradient(circle_at_68%_30%,rgba(139,198,232,0.95),transparent_38%),linear-gradient(145deg,#d2e7f5,#0066ac)]", "aria-hidden": "true" })), videoSrc ? (_jsx("span", { ref: fadeRef, className: "pointer-events-none absolute inset-0 rounded-[inherit] bg-white opacity-0", "aria-hidden": "true" })) : null] }) }));
}
/** The one-off, code-owned introduction for the public homepage. */
export const HeroMediaCentered = React.forwardRef(({ title = "Reimagining how dialysis is delivered", description = "Diality is a medical technology company rethinking hemodialysis from the ground up.", ctaLabel = "Our Story", ctaHref = "/our-story", videoSrc, posterSrc, className, ...props }, ref) => {
    const titleId = React.useId();
    return (_jsxs("section", { ref: ref, className: cn("mx-auto flex w-full max-w-[1300px] flex-col items-center gap-10 px-[clamp(24px,10.694vw,154px)] pt-[184px] max-[1160px]:px-m-gutter max-[1160px]:pb-20 max-[1160px]:pt-[140px]", className), "aria-labelledby": titleId, "data-hero-media-centered": true, ...props, children: [_jsxs("div", { className: "flex w-full max-w-[900px] flex-col items-center gap-4 pt-3.5 text-center", children: [_jsx(Reveal, { delay: 0.1, eager: true, children: _jsx("h1", { id: titleId, className: "type-display-title text-brand-blue", children: title }) }), _jsx(Reveal, { delay: 0.22, eager: true, children: _jsx("p", { className: "type-body-lg text-foreground min-[1161px]:whitespace-nowrap max-[900px]:text-lg", children: description }) }), _jsx(Reveal, { delay: 0.34, className: "mt-4", eager: true, children: _jsx(Button, { asChild: true, children: _jsx(BlockLink, { href: ctaHref, children: ctaLabel }) }) })] }), _jsx(Reveal, { delay: 0.1, className: "min-w-0 max-w-[300px] flex-1 max-[1160px]:flex-none", eager: true, children: _jsx(HeroMedia, { videoSrc: videoSrc, posterSrc: posterSrc }) })] }));
});
HeroMediaCentered.displayName = "HeroMediaCentered";
//# sourceMappingURL=hero-media-centered.js.map